import React, { useState, useMemo, useEffect } from "react";
import { LuPencil } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import { Formik } from "formik";
import * as Yup from "yup";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import NewTableComponent from "../../../../components/NewTableComponent";
import CustomModal from "../../../../components/CustomModal";
import Input from "../../../../components/input";
import Select from "../../../../components/Select";
import Button from "../../../../components/button";
import { apiService } from "../../../../services/apiService";
import { successNotify, errorNotify } from "../../../../utils/toast";
import MultiLineText from "../../../../components/MultiLineText";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import {
  formatProvinceLabel,
  getProvinceOption,
  normalizeProvinceValue,
  provinceOptions,
} from "../../../../utils/provinceOptions";

const taxRuleValidationSchema = Yup.object().shape({
  name: Yup.string().required("Tax Rule Name is required"),
  sourceProvince: Yup.string().required("Source Province is required"),
  destinationProvince: Yup.string().required("Destination Province/State is required"),
  rate: Yup.number()
    .typeError("Tax Percentage must be a number")
    .required("Tax Percentage is required")
    .min(0, "Tax Percentage cannot be negative")
    .max(100, "Tax Percentage cannot exceed 100"),
  taxRegistrationNumber: Yup.string().required("Tax Registration Number is required"),
  effectiveFrom: Yup.string().required("Effective From date is required"),
  effectiveTo: Yup.string().required("Effective To date is required"),
  status: Yup.string().required("Status is required"),
});

const safeArray = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeTaxRuleApiRow = (row) => {
  if (!row) return {};
  return {
    rule_id: row.id || row.rule_id || "",
    name: row.name || "",
    sourceProvince: normalizeProvinceValue(row.source_province || row.sourceProvince || ""),
    destinationProvince: normalizeProvinceValue(row.destination_province || row.destinationProvince || ""),
    rate: row.tax_percentage || row.rate || 0,
    taxRegistrationNumber: row.tax_number || row.taxRegistrationNumber || "",
    effectiveFrom: row.effective_from ? row.effective_from.split("T")[0] : "",
    effectiveTo: row.effective_to ? row.effective_to.split("T")[0] : "",
    status: row.status ? (row.status.charAt(0).toUpperCase() + row.status.slice(1).toLowerCase()) : "Active",
  };
};

const TaxRulesTab = ({ onSave, onDelete, addTrigger }) => {
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiRows, setApiRows] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    rule_id: "",
    name: "",
    sourceProvince: "",
    destinationProvince: "",
    rate: "",
    taxRegistrationNumber: "",
    effectiveFrom: "",
    effectiveTo: "",
    status: "",
  });
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Reset page when filter search parameters or limit changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  // Fetch paginated data from API
  useEffect(() => {
    let isMounted = true;

    const fetchTaxRules = async () => {
      setLoading(true);
      try {
        const response = await apiService.getTaxRulesPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters,
        });

        const payload = response?.data || {};
        const nextRows = safeArray(payload?.results || payload?.data).map(normalizeTaxRuleApiRow);

        if (!isMounted) return;

        setApiRows(nextRows);
        setMeta({
          totalItems: Number(payload?.meta?.totalItems || payload?.total || nextRows.length),
          totalPages: Number(payload?.meta?.totalPages || 1),
        });
      } catch (error) {
        if (!isMounted) return;
        setApiRows([]);
        setMeta({ totalItems: 0, totalPages: 1 });
        errorNotify(error || "Failed to fetch tax rules");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTaxRules();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchText, searchFilters, refetchTrigger]);

  // React to add trigger from parent
  useEffect(() => {
    if (addTrigger > 0) {
      handleOpenAdd();
    }
  }, [addTrigger]);

  const handleOpenAdd = () => {
    setInitialFormValues({
      rule_id: "",
      name: "",
      sourceProvince: "",
      destinationProvince: "",
      rate: "",
      taxRegistrationNumber: "",
      effectiveFrom: "",
      effectiveTo: "",
      status: "",
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = async (row) => {
    try {
      setLoading(true);
      const res = await apiService.getTaxRuleDetail(row.rule_id);
      const detail = res?.data || {};
      setInitialFormValues({
        rule_id: detail.id || "",
        name: detail.name || "",
        sourceProvince: normalizeProvinceValue(detail.source_province || ""),
        destinationProvince: normalizeProvinceValue(detail.destination_province || ""),
        rate: detail.tax_percentage || 0,
        taxRegistrationNumber: detail.tax_number || "",
        effectiveFrom: detail.effective_from ? detail.effective_from.split("T")[0] : "",
        effectiveTo: detail.effective_to ? detail.effective_to.split("T")[0] : "",
        status: detail.status ? String(detail.status).toLowerCase() : "active",
      });
      setShowFormModal(true);
    } catch (error) {
      errorNotify(error || "Failed to fetch tax rule details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        name: values.name,
        source_province: normalizeProvinceValue(values.sourceProvince),
        destination_province: normalizeProvinceValue(values.destinationProvince),
        tax_number: values.taxRegistrationNumber,
        tax_percentage: Number(values.rate),
        effective_from: values.effectiveFrom,
        effective_to: values.effectiveTo,
        status: values.status,
      };

      if (values.rule_id) {
        // Update API integration
        const updatePayload = {
          id: Number(values.rule_id),
          ...payload,
        };
        await apiService.updateTaxRule(updatePayload);
        successNotify("Tax Rule updated successfully");
      } else {
        // Create API integration
        await apiService.createTaxRule(payload);
        successNotify("Tax Rule created successfully");
      }
      setShowFormModal(false);

      if (currentPage === 1) {
        setRefetchTrigger((prev) => prev + 1);
      } else {
        setCurrentPage(1);
      }
    } catch (error) {
      errorNotify(error || "Failed to save tax rule");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteTaxRule(deleteTargetId);
      successNotify("Tax Rule deleted successfully");
      setShowDeleteModal(false);
      setDeleteTargetId(null);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to delete tax rule");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      name: "Rule Name",
      key: "name",
      selector: (row) => row.name || "",
      cell: (row) => (
        <MultiLineText text={row.name || "-"} maxWidth="150px" />
      ),
      tableColumn: true,
      filterName: "nameFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Route",
      key: "route",
      selector: (row) =>
        `${formatProvinceLabel(row.sourceProvince) || "Any"} -> ${formatProvinceLabel(row.destinationProvince) || "Any"}`,
      cell: (row) => (
        <span>
          {formatProvinceLabel(row.sourceProvince) || "Any"} &rarr; {formatProvinceLabel(row.destinationProvince) || "Any"}
        </span>
      ),
      tableColumn: true,
      filterName: "routeFilter",
      type: "text",
      minWidth: "200px",
    },
    {
      name: "Tax Rate",
      key: "rate",
      selector: (row) => row.rate || 0,
      cell: (row) => `${row.rate}%`,
      tableColumn: true,
      filterName: "rateFilter",
      type: "number",
      minWidth: "120px",
    },
    {
      name: "Tax Registration",
      key: "taxRegistrationNumber",
      selector: (row) => row.taxRegistrationNumber || "-",
      tableColumn: true,
      filterName: "taxRegistrationNumberFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => row.status,
      cell: (row) => (
        <span
          className={`settings_badge ${String(row.status).toLowerCase() === "active"
            ? "settings_badge_active"
            : "settings_badge_inactive"
            }`}
        >
          {row.status}
        </span>
      ),
      tableColumn: true,
      filterName: "statusFilter",
      type: "select",
      options: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" }
      ],
      minWidth: "130px",
    },
    {
      name: "Action",
      key: "action",
      cell: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="edit_btn"
            title="Edit rule"
            onClick={() => handleOpenEdit(row)}
          >
            <LuPencil />
          </button>
          <button
            type="button"
            className="delete_btn"
            title="Delete rule"
            onClick={() => handleOpenDelete(row.rule_id)}
          >
            <RiDeleteBinLine />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <div className="mt-2">
      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={apiRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={meta.totalItems}
        totalPages={meta.totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[10, 20, 50]}
        showFilter={false}
        showPagination={true}
      />

      <CustomModal
        show={showFormModal}
        handleClose={() => setShowFormModal(false)}
        heading={initialFormValues.rule_id ? "Edit Tax Rule" : "Add Tax Rule"}
        size="lg"
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={taxRuleValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            submitForm,
          }) => (
            <div>
              <Row>
                <Col md={6}>
                  <Input
                    label="Tax Rule Name"
                    placeholder="e.g. Sindh to Sindh SRB"
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.name}
                    touched={touched.name}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Input
                    label="Tax Percentage (SST)"
                    placeholder="e.g. 15"
                    type="number"
                    name="rate"
                    value={values.rate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.rate}
                    touched={touched.rate}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Select
                    label="Source Province/State"
                    placeholder="Select Source Province"
                    options={provinceOptions}
                    value={getProvinceOption(values.sourceProvince)}
                    name="sourceProvince"
                    onChange={(option) =>
                      setFieldValue("sourceProvince", option ? option.value : "")
                    }
                    onBlur={handleBlur}
                    error={errors.sourceProvince}
                    touched={touched.sourceProvince}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label="Province/State"
                    placeholder="Select Destination Province"
                    options={provinceOptions}
                    value={getProvinceOption(values.destinationProvince)}
                    name="destinationProvince"
                    onChange={(option) =>
                      setFieldValue(
                        "destinationProvince",
                        option ? option.value : ""
                      )
                    }
                    onBlur={handleBlur}
                    error={errors.destinationProvince}
                    touched={touched.destinationProvince}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Input
                    label="Tax Registration Number"
                    placeholder="TRN-XXXXXXX"
                    type="text"
                    name="taxRegistrationNumber"
                    value={values.taxRegistrationNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.taxRegistrationNumber}
                    touched={touched.taxRegistrationNumber}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Input
                    label="Effective From"
                    type="date"
                    name="effectiveFrom"
                    value={values.effectiveFrom}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.effectiveFrom}
                    touched={touched.effectiveFrom}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label="Effective To"
                    type="date"
                    name="effectiveTo"
                    value={values.effectiveTo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.effectiveTo}
                    touched={touched.effectiveTo}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Select
                    label="Status"
                    placeholder="Select Status"
                    options={statusOptions}
                    value={
                      statusOptions.find((opt) => opt.value === values.status) ||
                      null
                    }
                    name="status"
                    onChange={(option) =>
                      setFieldValue("status", option ? option.value : "")
                    }
                    onBlur={handleBlur}
                    error={errors.status}
                    touched={touched.status}
                    required
                  />
                </Col>

                <Col md={12}>
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      cancelBtn={() => setShowFormModal(false)}
                      submitBtn={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        submitForm();
                      }}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      submitText="Save"
                      type="button"
                      shouldRow={false}
                      buttonExtraCss="fuel_add_btn"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Formik>
      </CustomModal>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Tax Rule"
        description="Are you sure you want to delete this tax rule? This action cannot be undone."
      />
    </div>
  );
};

export default TaxRulesTab;
